/**
 * You against one player: the two figures, every round you both bet in, and
 * where they stand with everyone else.
 *
 * ROUND BY ROUND IS FROM YOUR POINT OF VIEW and its column sums exactly to V
 * YOU — the handoff calls that an invariant and the server pins it with a test.
 * It counts only what passed BETWEEN THE TWO OF YOU: a pairs bet also moves
 * money against the other opponent, and that is not this column's business.
 *
 * Read-only. This is a statement of position, not a bill.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useApi } from '../useApi';
import { Avatar, money } from './primitives';
import { shortName } from './format';
import { roundsLabel } from './history';

export const PlayerSheet = ({
  playerId, name, rounds, vs, net, nameOf, onClose,
}) => {
  const { authenticatedFetch } = useApi();
  const [detail, setDetail] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authenticatedFetch(
          `/predictions/history/${playerId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (!cancelled) setDetail(body);
      } catch (err) {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [playerId, authenticatedFetch]);

  const subline = rounds
    ? `${roundsLabel(rounds)} · you are ${
        vs > 0 ? 'up' : vs < 0 ? 'down' : 'level'}${
        vs === 0 ? '' : ` ${money(Math.abs(vs))}`}`
    : 'No bets with you yet';

  return createPortal(
    <>
      <button type="button" className="pr-scrim" aria-label="Close"
              onClick={onClose} />
      <div className="pr-sheet pr-page" role="dialog" aria-modal="true">
        <div className="pr-sheet-handle" />

        <div className="pr-sheet-head">
          <Avatar name={name} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="pr-sheet-title">{name}</div>
            <div className="pr-sheet-sub">{subline}</div>
          </div>
          <button type="button" className="pr-text-link" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="pr-sheet-tiles">
          <div className="pr-sheet-tile">
            <div className="pr-tile-label">Season net</div>
            <div className={`pr-tile-big${
              net > 0 ? ' is-up' : net < 0 ? ' is-down' : ''}`}>
              {net > 0 ? '+' : net < 0 ? '−' : ''}{money(Math.abs(net))}
            </div>
          </div>
          <div className="pr-sheet-tile">
            <div className="pr-tile-label">V you</div>
            <div className={`pr-tile-big${
              vs > 0 ? ' is-up' : vs < 0 ? ' is-down' : ''}`}>
              {vs > 0 ? '+' : vs < 0 ? '−' : ''}{money(Math.abs(vs))}
            </div>
          </div>
        </div>

        {failed ? (
          <div className="pr-error">Could not load the detail.</div>
        ) : null}

        {detail ? (
          <>
            <div className="pr-eyebrow">Round by round</div>
            <div className="pr-inset-list">
              {detail.rounds.length === 0 ? (
                <div className="pr-empty">No rounds together yet.</div>
              ) : detail.rounds.map((round) => (
                <div key={round.key} className="pr-inset-row">
                  <span className="pr-row-main">
                    <span className="pr-row-title">
                      {round.course_name || round.name}
                    </span>
                    <span className="pr-row-sub">
                      {[round.date, round.kinds.join(', ')]
                        .filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className={`pr-amount-figure${
                    round.amount > 0 ? ' is-up'
                      : round.amount < 0 ? ' is-down' : ''}`}>
                    {round.amount === 0 ? '—'
                      : `${round.amount > 0 ? '+' : '−'}${
                          money(Math.abs(round.amount))}`}
                  </span>
                </div>
              ))}
            </div>

            {detail.their_positions.length ? (
              <>
                <div className="pr-eyebrow">
                  Where {shortName(name)} stands
                </div>
                <div className="pr-inset-list">
                  {detail.their_positions.map((p) => {
                    const up = p.up === playerId;
                    const other = up ? p.down : p.up;
                    return (
                      <div key={`${p.down}:${p.up}`} className="pr-inset-row">
                        <Avatar name={nameOf(other)} size={26} />
                        <span className="pr-row-main">
                          <span className="pr-row-title">{nameOf(other)}</span>
                        </span>
                        <span className={`pr-amount-figure${
                          up ? ' is-up' : ' is-down'}`}>
                          {up ? '+' : '−'}{money(p.amount)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : null}
          </>
        ) : !failed ? (
          <div className="pr-skeleton" style={{ height: 120 }} />
        ) : null}
      </div>
    </>,
    document.body,
  );
};

export default PlayerSheet;
